import type { Dayjs } from "dayjs";

import { twMerge } from "tailwind-merge";

import { useUserTimezone } from "@/hooks/useUserTimezone";

import type { ScheduledPost } from "./scheduledPost";

import { CalendarEvent } from "./CalendarEvent";
import { inTimeZone, isSameDay } from "./timeZone";

export function WeekView({
  date,
  onSelectPost,
  posts,
  selectedPostId,
}: {
  date: Dayjs;
  onSelectPost: (post: ScheduledPost) => void;
  posts: ScheduledPost[];
  selectedPostId?: number;
}) {
  const startOfWeek = date.startOf("week");
  const days = Array.from({ length: 7 }, (_, index) =>
    startOfWeek.add(index, "day"),
  );
  const { timezone } = useUserTimezone();
  const today = inTimeZone(timezone);

  return (
    <div className="flex flex-col">
      <div className="grid grid-cols-7 border-b border-border-secondary">
        {days.map((day) => (
          <div
            className={twMerge(
              "flex flex-col items-center gap-xxs border-l border-border-secondary py-md first:border-l-0 text-sm font-medium text-text-secondary",
              isSameDay(day, today) &&
                "bg-bg-brand-primary text-text-brand-secondary",
            )}
            key={day.toString()}
          >
            <span>{day.format("ddd")}</span>
            <span
              className={twMerge(
                "text-lg font-semibold text-text-primary",
                isSameDay(day, today) && "text-text-brand-secondary",
              )}
            >
              {day.format("D")}
            </span>
          </div>
        ))}
      </div>

      <div className="grid min-h-[480px] grid-cols-7">
        {days.map((day) => (
          <div
            className={twMerge(
              "flex min-w-0 flex-col gap-xs border-l border-border-secondary p-xs first:border-l-0",
              isSameDay(day, today) && "bg-bg-brand-primary/40",
            )}
            key={day.toString()}
          >
            {posts
              .filter((post) =>
                isSameDay(inTimeZone(timezone, post.scheduledAt), day),
              )
              .sort((a, b) => a.scheduledAt.getTime() - b.scheduledAt.getTime())
              .map((post) => (
                <CalendarEvent
                  isSelected={post.id === selectedPostId}
                  key={post.id}
                  onPress={() => onSelectPost(post)}
                  post={post}
                />
              ))}
          </div>
        ))}
      </div>
    </div>
  );
}
